import React, { useEffect, useState, useContext, useRef } from "react";
import {
  ThemeContext,
  PatientContext,
  InputVisibleContext,
} from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";

import { useHistory, useLocation } from "react-router-dom";
import "react-day-picker/lib/style.css";
import { Row, Col, Alert } from "react-bootstrap";

//components
import Sallie from "../UIComponents/Sallie";
import GoalList from "./GoalList";
import FeelingToday from "./FeelingToday";
import Calendar from "./Calendar";
import EditNotification from "./EditNotification";
import SymptomTracker from "./SymptomTracker";
import DaysFromInjury from "./DaysFromInjury";
import ComponentWrapper from "./ComponentWrapper";
import AdditionalNote from "./AdditionalNote";
import TriggerSelector from "./TriggerSelector";
import SuggestionBox from "./SuggestionBox";
import ThemeChanger from "../UIComponents/ThemeChanger";
import AddSymptoms from "./AddSymptoms";
import Charts from "./Charts";
import LoadingSpinner from "../UIComponents/LoadingSpinner";
import CurrentPatient from "./CurrentPatient";
import ReferPatientButton from "./ReferPatientButton";
import OnOffButtonMessages from "./OnOffButtonMessages";
import OnOffButtonAlert from "./OnOffButtonAlert";
import OnOffButtonReferralRecord from "./OnOffButtonReferralRecord";
import PatientStatus from "./PatientStatus";
import InviteProvider from "./InviteProvider";
import InviteTherapist from "./InviteTherapist";
import InvitePatient from "./InvitePatient";

//lib
import customizeMessages from "../../lib/customizeMessages";
import useWindowSize from "../../lib/useWindowSize";
import getLoggedDataToday from "../../lib/getLoggedDataToday";

//api
import { getAdditionalNote } from "../../api/TBIRequests";
import { getPatientRegistrationProgress } from "../../api/TBIRequests";
import emailAlert from "../../images/email-notification.svg";
import theme from "../../index.scss";

const Dashboard = ({
  user,
  loadTrackedData,
  loadingTrackedData,
  trackedData,
  errorLoadingTrackedData,
  topSymptoms,
  setTopSymptoms,
  worstSymptoms,
  setWorstSymptoms,
  loadingTopSymptoms,
  loadingFeelingToday,
  doneSimpleTracking,
  setDoneSimpleTracking,
  loadingTopTrigger,
  topTrigger,
  errorLoadingTopTrigger,
  loadTrigger,
  messages,
  setTherapistList,
  setCaregiverList,
  setPatientList,
  patientList,
  caregiverList,
  therapistList,
  onOffButtonMessagesStatus,
  setOnOffButtonMessagesStatus,
  inputVisible,
  setInputVisible,
}) => {
  const { patient } = useContext(PatientContext);
  const { t } = useTranslation();
  const { dark } = useContext(ThemeContext);

  const [width] = useWindowSize();
  const history = useHistory();
  const location = useLocation();

  // tied to calendar components
  const [date, setDate] = useState(new Date());

  // start with the 0 index of the user's top symptoms and update as needed
  const [currentTrackingSymptom, setCurrentTrackingSymptom] = useState(0);

  // initialized to one because we dont use it until at least one symptom
  // has been tracked
  const [currentTrackingStep, setCurrentTrackingStep] = useState(0);
  const [additionalNotes, setAdditionalNotes] = useState([]);
  const [showPopUp, setShowPopUp] = useState(true);
  const [status, setStatus] = useState("Active");
  const [firstUnfinishedStep, setFirstUnfinishedStep] = useState("");
  const unreadMessagesPresent =
    messages && messages.some((message) => !message.hasBeenRead);
  const [skippedUnloggedSymptoms, setSkippedUnloggedSymptoms] = useState(0);

  // lifted from SymptomTracker so it persists across symptom advances
  const [trackingMode, setTrackingMode] = useState(null); // null | "top3" | "all"
  const [top3Step, setTop3Step] = useState(0);

  // Set right before finishTop3TrackingStep does its own explicit state
  // transition, consumed (and reset) the next time RESUME-EFFECT's
  // allWorstLogged branch runs. Prevents RESUME-EFFECT from re-deriving
  // and re-applying the same "top3 complete" transition a second time
  // off the loadTrackedData() that immediately follows. Does not affect
  // any other branch (mid-"all" mode, partial top3, or a genuine resume
  // on mount/reload, where this ref is simply false).
  const justFinishedTop3Ref = useRef(false);

  //add more symptoms,if there's no tracking factors,then refresh the page
  if (location?.state?.fromPatientDashboard == true) {
    window.location.reload();
  }
  const handleDayClick = (day, { disabled }) => {
    if (disabled) {
      return;
    }
    setDate(day);
  };

  useEffect(() => {
    let isMounted = true; // Add a flag to track if the component is mounted
    // Use the isMounted flag in your asynchronous operation
    getPatientRegistrationProgress(patient?.patientId)
      .then(({ data }) => {
        if (isMounted) {
          // Check if the component is still mounted before updating state
          setFirstUnfinishedStep(data);
        }
      })
      .catch((e) => console.log("getPatientRegistrationProgress", e));

    return () => {
      isMounted = false; // Set the flag to false when the component is unmounted
    };
  }, [patient]);

  if (patient && firstUnfinishedStep.firstFalseStep != "finished") {
    if (firstUnfinishedStep.firstFalseStep == "step1") {
      history.push("/BasicInfo");
    }
    if (firstUnfinishedStep.firstFalseStep == "step2") {
      if (patient.patientType == "Stroke") {
        history.push("/RecentStroke1", {
          fromPatientControl: true, // add this to skip step7b-Notification preference
        });
      } else if (patient.patientType == "TBI/Concussion") {
        history.push("/RecentTBI1", {
          fromPatientControl: true, // add this to skip step7b-Notification preference
        });
      } else if (patient.patientType == "SMI") {
        history.push("/RecentSMI1", {
          fromPatientControl: true, // add this to skip step7b-Notification preference
        });
      } else {
        //Dementia and Other
        history.push("/RecentCondition1", {
          fromPatientControl: true, // add this to skip step7b-Notification preference
        });
      }
    }
    if (firstUnfinishedStep.firstFalseStep == "step3") {
      if (patient.patientType == "Stroke") {
        history.push("/RecentStroke2", {
          fromPatientControl: true, // add this to skip step7b-Notification preference
        });
      } else if (patient.patientType == "TBI/Concussion") {
        history.push("/RecentTBI2", {
          fromPatientControl: true, // add this to skip step7b-Notification preference
        });
      } else if (patient.patientType == "SMI") {
        // SMI2 sets no step, so step3-incomplete resumes at SMI2 (then SMI3)
        history.push("/RecentSMI2", {
          fromPatientControl: true, // add this to skip step7b-Notification preference
        });
      } else {
        //Dementia and Other
        history.push("/RecentCondition2", {
          fromPatientControl: true, // add this to skip step7b-Notification preference
        });
      }
    }
    if (firstUnfinishedStep.firstFalseStep == "step4") {
      history.push("/laterSymptoms", {
        fromPatientControl: true, // add this to skip step7b-Notification preference
      });
    }
    if (firstUnfinishedStep.firstFalseStep == "step5") {
      history.push("/laterSymptoms", {
        fromPatientControl: true, // add this to skip step7b-Notification preference
      }); //not /selectTopSymptoms because it needs info from step4
    }
    if (firstUnfinishedStep.firstFalseStep == "step6") {
      history.push("/selectTrigger", {
        fromPatientControl: true, // add this to skip step7b-Notification preference
      });
    }
    if (firstUnfinishedStep.firstFalseStep == "step7") {
      history.push("/selectTherapies", {
        fromPatientControl: true, // add this to skip step7b-Notification preference
      });
    }
  }

  useEffect(() => {
    let isMounted = true; // Add a flag to track if the component is mounted
    // Use the isMounted flag in your asynchronous operation
    getAdditionalNote({
      patientId: patient?.patientId,
      therapistId: user.userType === "Therapist" ? user.userId : null,
    })
      .then(({ data }) => {
        if (isMounted) {
          // Check if the component is still mounted before updating state
          setAdditionalNotes(data);
        }
      })
      .catch((e) => console.log("getAdditionalNotesError", e));

    return () => {
      isMounted = false; // Set the flag to false when the component is unmounted
    };
  }, [patient]);

  useEffect(() => {
    let isMounted = true;
    console.log(
      "[RESUME-EFFECT] fired. trackingMode=",
      trackingMode,
      "top3Step=",
      top3Step,
      "skippedUnloggedSymptoms=",
      skippedUnloggedSymptoms,
      "topSymptoms.length=",
      topSymptoms && topSymptoms.length,
      "worstSymptoms.length=",
      worstSymptoms && worstSymptoms.length
    );
    if (topSymptoms && topSymptoms.length > 0 && trackedData) {
      const loggedSymptomToday = getLoggedDataToday(
        trackedData,
        "medical",
        date
      );
      console.log(
        "[RESUME-EFFECT] loggedSymptomToday.length=",
        loggedSymptomToday.length,
        "factors=",
        loggedSymptomToday.map((s) => s.factor)
      );
      if (loggedSymptomToday.length === 0 && isMounted) {
        // Nothing logged yet — start fresh, show mode selector
        console.log("[RESUME-EFFECT] branch: nothing logged yet -> reset to topSymptoms[0]", topSymptoms[0] && topSymptoms[0].factor);
        setCurrentTrackingSymptom(topSymptoms[0]);
        setCurrentTrackingStep(0);
        setSkippedUnloggedSymptoms(0);
      } else if (isMounted) {
        // Something has been logged. Check if worstSymptoms are all done (top3 mode completed)
        const allWorstLogged =
          worstSymptoms &&
          worstSymptoms.length > 0 &&
          worstSymptoms.every((ws) =>
            loggedSymptomToday.some((ls) => ls.factor === ws.factor)
          );
        const allTopSymptomsLogged =
          topSymptoms.length > 0 &&
          topSymptoms.every((ts) =>
            loggedSymptomToday.some((ls) => ls.factor === ts.factor)
          );
        console.log(
          "[RESUME-EFFECT] allWorstLogged=", allWorstLogged,
          "allTopSymptomsLogged=", allTopSymptomsLogged
        );

        if (trackingMode === "all" && !allTopSymptomsLogged) {
          // Actively mid "all" session with symptoms still remaining.
          // allWorstLogged only reflects the worst-3 subset, not the full
          // "all" list — completing the worst-3 subset early (just a
          // coincidence of tracking order) must NOT short-circuit an
          // in-progress "all" session that still has more to track.
          console.log("[RESUME-EFFECT] branch: mid 'all' mode with symptoms remaining -> saveTrackingStep()");
          saveTrackingStep();
        } else if (allWorstLogged) {
          if (justFinishedTop3Ref.current) {
            // finishTop3TrackingStep just handled this exact transition
            // explicitly (see its own state updates above) — this fire is
            // the loadTrackedData() echo of that, not a genuine resume.
            // Consume the flag and skip so we don't re-arm trackingMode
            // back to "top3" behind the explicit path's back.
            justFinishedTop3Ref.current = false;
            console.log("[RESUME-EFFECT] branch: allWorstLogged but top3 was just finished explicitly -> skipping re-arm");
          } else {
            // top3 flow was completed (or "all" is also fully done) — restore
            // mode and skip straight to trigger/done check.
            // Pass 'top3-resume' explicitly so finishTrackingStep does not read stale
            // currentTrackingSymptom closure state (which starts as 0 on mount).
            console.log("[RESUME-EFFECT] branch: allWorstLogged -> setTrackingMode(top3) + finishTrackingStep('top3-resume')");
            setTrackingMode("top3");
            finishTrackingStep("top3-resume");
          }
        } else {
          // "all" mode, or top3 partially done — resume normal flow
          console.log("[RESUME-EFFECT] branch: NOT allWorstLogged -> saveTrackingStep() [this recalculates against FULL topSymptoms, ignores top3Step]");
          saveTrackingStep();
        }
      }
    }
    return () => {
      isMounted = false;
    };
  }, [history, topSymptoms, worstSymptoms, trackedData, date]);

  useEffect(() => {
    // TODO: This is a bandaid for resetting on date change, this should be removed in the future. (When doing so, re-add date to the previous useEffect)
    if (topSymptoms && topSymptoms.length > 0 && trackedData) {
      setCurrentTrackingSymptom(topSymptoms[0]);
      setCurrentTrackingStep(0);
      setSkippedUnloggedSymptoms(0);
      // Reset the mode selection on date change so untracked days show the
      // mode selector again instead of carrying over the previously
      // selected date's trackingMode. RESUME-EFFECT (above) independently
      // re-derives and restores trackingMode from that date's trackedData
      // if the day is already fully logged, so this doesn't fight it.
      setTrackingMode(null);
      setTop3Step(0);
      prepareNextSymptomTrackingStep(0);
    }
  }, [date]);

  function prepareNextSymptomTrackingStep(skipped) {
    const loggedSymptomToday = getLoggedDataToday(trackedData, "medical", date);

    const unloggedSymptoms = topSymptoms.filter((topSymptom) => {
      return !loggedSymptomToday.some((loggedSymptom) => {
        return loggedSymptom.factor === topSymptom.factor;
      });
    });
    const numLoggedSymptoms = topSymptoms.length - unloggedSymptoms.length;

    console.log(
      "[PREPARE-NEXT] called with skipped=",
      skipped,
      "topSymptoms.length=",
      topSymptoms.length,
      "unloggedSymptoms=",
      unloggedSymptoms.map((s) => s.factor),
      "numLoggedSymptoms=",
      numLoggedSymptoms,
      "current trackingMode=",
      trackingMode,
      "current top3Step=",
      top3Step
    );

    setSkippedUnloggedSymptoms(skipped);
    setCurrentTrackingStep(numLoggedSymptoms + skipped);

    const nextSymptom = unloggedSymptoms[skipped];
    if (!nextSymptom) {
      console.log("[PREPARE-NEXT] no nextSymptom found at index", skipped, "-> calling finishTrackingStep() EARLY (this can prematurely skip to SDOH/done even mid-top3)");
      finishTrackingStep();
    } else {
      console.log("[PREPARE-NEXT] setting currentTrackingSymptom ->", nextSymptom.factor, "(NOTE: ignored by SymptomTracker while trackingMode === 'top3')");
      setCurrentTrackingSymptom(nextSymptom);
    }
  }

  const finishTrackingStep = (calledFromSymbol) => {
    const loggedTriggerToday = getLoggedDataToday(trackedData, "SDOH", date);
    // Use the explicitly passed symbol if provided, otherwise fall back to current state.
    // This avoids stale closure issues when called from within a useEffect.
    const effectiveSymbol = calledFromSymbol ?? currentTrackingSymptom;
    console.log(
      "[FINISH-TRACKING-STEP] calledFromSymbol=",
      calledFromSymbol,
      "effectiveSymbol=",
      typeof effectiveSymbol === "object" ? effectiveSymbol?.factor : effectiveSymbol,
      "loggedTriggerToday.length=",
      loggedTriggerToday.length
    );
    if (loggedTriggerToday.length || effectiveSymbol === "SDOH") {
      console.log("[FINISH-TRACKING-STEP] -> setCurrentTrackingSymptom('done')");
      setCurrentTrackingSymptom("done");
    } else {
      console.log("[FINISH-TRACKING-STEP] -> setCurrentTrackingSymptom('SDOH')");
      setCurrentTrackingSymptom("SDOH");
    }
  };

  const skipTrackingStep = () => {
    prepareNextSymptomTrackingStep(skippedUnloggedSymptoms + 1);
  };

  const saveTrackingStep = () => {
    prepareNextSymptomTrackingStep(skippedUnloggedSymptoms);
  };

  // Called when top3 mode finishes — bypasses prepareNextSymptomTrackingStep
  // destination: "SDOH" after symptoms are done, "done" after triggers are done
  const finishTop3TrackingStep = (destination) => {
    console.log("[FINISH-TOP3] called with destination=", destination);
    justFinishedTop3Ref.current = true;
    setTrackingMode(null);
    setTop3Step(0);
    if (destination) {
      setCurrentTrackingSymptom(destination);
    } else {
      finishTrackingStep();
    }
  };

  // Extracting properties from the user object
  const { userType, messageRecipient } = user;

  const ConditionalFeelingToday =
    status != "Released" &&
    (userType == "patient" ||
      userType == "Therapist" ||
      userType == "caregiver") &&
    (loadingFeelingToday ? (
      <LoadingSpinner />
    ) : (
      !doneSimpleTracking && (
        <FeelingToday
          setDoneSimpleTracking={setDoneSimpleTracking}
          date={date}
          user={user}
        />
      )
    ));

  const ConditionalSymptomTracker =
    status != "Released" &&
    (userType == "patient" ||
      userType == "Therapist" ||
      userType == "caregiver") &&
    (loadingTopSymptoms || loadingTrackedData || loadingTopTrigger ? (
      <LoadingSpinner />
    ) : (
      <>
        {currentTrackingSymptom === "SDOH" && (
          <ComponentWrapper>
            <TriggerSelector
              topTrigger={topTrigger}
              userType={userType}
              errorLoadingTopTrigger={errorLoadingTopTrigger}
              loadTrigger={loadTrigger}
              date={date}
              skipTrackingStep={skipTrackingStep}
              saveTrackingStep={saveTrackingStep}
              finishTop3TrackingStep={finishTop3TrackingStep}
              trackingMode={trackingMode}
              loadTrackedData={loadTrackedData}
            />
          </ComponentWrapper>
        )}
        {currentTrackingSymptom !== "SDOH" &&
          currentTrackingSymptom !== "done" &&
          currentTrackingSymptom !== 0 && (
            <ComponentWrapper>
              <SymptomTracker
                currentTrackingStep={currentTrackingStep}
                topSymptoms={topSymptoms}
                worstSymptoms={worstSymptoms}
                date={date}
                symptom={currentTrackingSymptom}
                trackedData={trackedData}
                skipTrackingStep={skipTrackingStep}
                saveTrackingStep={saveTrackingStep}
                finishTop3TrackingStep={finishTop3TrackingStep}
                loadTrackedData={loadTrackedData}
                trackingMode={trackingMode}
                setTrackingMode={setTrackingMode}
                top3Step={top3Step}
                setTop3Step={setTop3Step}
              />
            </ComponentWrapper>
          )}
        {currentTrackingSymptom === "done" && (
          <ComponentWrapper>
            <AdditionalNote date={date} user={user} />
          </ComponentWrapper>
        )}
      </>
    ));

  // trackingMode stays set to "top3"/"all" after today's symptoms are fully
  // logged (restored by RESUME-EFFECT so the trigger/AdditionalNote screens
  // know which mode was used) — but that no longer means the patient is
  // "midway through" adding symptoms. Only bypass the top3-vs-total ask in
  // AddSymptoms while they're still actively on a real symptom step.
  const isActivelyTrackingSymptoms =
    trackingMode !== null &&
    currentTrackingSymptom !== "SDOH" &&
    currentTrackingSymptom !== "done" &&
    currentTrackingSymptom !== 0;

  const ConditionalAddSymptoms = status != "Released" &&
    (userType == "patient" ||
      userType == "Therapist" ||
      userType == "caregiver") && (
      <AddSymptoms
        topSymptoms={topSymptoms}
        setTopSymptoms={setTopSymptoms}
        worstSymptoms={worstSymptoms}
        setWorstSymptoms={setWorstSymptoms}
        trackingMode={isActivelyTrackingSymptoms ? trackingMode : null}
        user={user}
      />
    );

  const ConditionalCombinedChart =
    user.noPatientProvider !== "noPatient" &&
    (loadingTrackedData || loadingTopSymptoms ? (
      <LoadingSpinner />
    ) : (
      <Charts
        topSymptoms={topSymptoms}
        data={trackedData}
        error={errorLoadingTrackedData}
        retry={loadTrackedData}
        additionalNotes={additionalNotes}
        user={user}
      />
    ));

  const ConditionalCurrentPatient = () =>
    user.noPatientProvider !== "noPatient" &&
    (userType === "caregiver" ||
      userType === "provider" ||
      userType === "Therapist") && (
      <Col md={3}>
        <CurrentPatient userType={userType} />
      </Col>
    );

  const ConditionalInviteProvider = () =>
    userType !== "provider" && (
      <InputVisibleContext.Provider value={{ inputVisible }}>
        <Col md={3}>
          <InviteProvider user={user} setInputVisible={setInputVisible} />
        </Col>
      </InputVisibleContext.Provider>
    );

  const ConditionalInviteTherapist = () =>
    userType !== "Therapist" && (
      <Col md={3}>
        <InviteTherapist user={user} />
      </Col>
    );

  const ConditionalInvitePatient = () =>
    (userType === "Therapist" || userType === "provider") && (
      <Col md={3}>
        <InvitePatient user={user} />
      </Col>
    );

  const ConditionalReferPatientButton = () =>
    user.noPatientProvider !== "noPatient" &&
    (userType === "provider" || userType === "Therapist") && (
      <Col md={3}>
        <ReferPatientButton setInputVisible={setInputVisible} />
      </Col>
    );

  const ConditionalOnOffButtonMessages = () =>
    (userType === "Therapist" || userType === "provider") && (
      <OnOffButtonMessages
        onTitle={t("dashboard.toggle_messages.on")}
        offTitle={t("dashboard.toggle_messages.off")}
        caregiverList={caregiverList}
        therapistList={therapistList}
        // setHideForProviderThatTurnMessageOff={setHideForProviderThatTurnMessageOff}
        onOffButtonMessagesStatus={onOffButtonMessagesStatus}
        setOnOffButtonMessagesStatus={setOnOffButtonMessagesStatus}
      />
    );

  const ConditionalOnOffButtonAlert = () =>
    (userType === "Therapist" || userType === "provider") && (
      <OnOffButtonAlert
        onTitle={t("dashboard.toggle_alerts.on")}
        offTitle={t("dashboard.toggle_alerts.off")}
      />
    );

  const ConditionalOnOffButtonReferralRecord = () =>
    (userType === "provider" || userType === "Therapist") && (
      <OnOffButtonReferralRecord />
    );

  const ConditionalPatientStatus = () =>
    user.noPatientProvider !== "noPatient" &&
    (userType === "provider" || userType === "Therapist") && (
      <PatientStatus
        status={status}
        setStatus={setStatus}
        userType={userType}
        patientId={patient?.patientId}
        therapistList={therapistList}
        patientList={patientList}
        caregiverList={caregiverList}
        setTherapistList={setTherapistList}
        setCaregiverList={setCaregiverList}
        setPatientList={setPatientList}
      />
    );

  const ConditionalCalendar = () => {
    if (userType !== "provider") {
      return <Calendar selectedDays={date} onDayClick={handleDayClick} />;
    }
    return null;
  };

  const ConditionalSendMessage = () => (
    <div className="d-flex justify-content-center pt-2 pb-2 pl-1 pr-1">
      <h6
        className="pt-2 pb-2 pl-3 pr-3"
        style={{
          width: "100%",
          height: "100%",
          borderRadius: "20px",
          backgroundColor: dark
            ? theme.darkModePrimary
            : unreadMessagesPresent
              ? "#3EB1A6"
              : "#ef5866",
          color: dark ? theme.darkModeText : "white",
          textAlign: "center",
          overflowWrap: "break-word",
          cursor: "pointer",
        }}
        onClick={() =>
          history.push("/messages", {
            curPatient: patient,
          })
        }
      >
        {unreadMessagesPresent ? (
          <div>
            {t("dashboard.mailbox.new")}
            <img
              style={{
                maxHeight: "30px",
                maxWidth: "30px",
                display: "inline-block",
                paddingLeft: 10,
              }}
              src={emailAlert}
              alt="email alert"
            />
          </div>
        ) : (
          t("dashboard.mailbox.send_or_read")
        )}
      </h6>
    </div>
  );

  const ConditionalSendMessagePatient = () => (
    <Col md={3}>
      <div className="d-flex justify-content-center">
        <h6
          className="pt-2 pb-2 pl-3 pr-3"
          style={{
            width: "75%",
            height: "100%",
            borderRadius: "20px",
            backgroundColor: dark
              ? theme.darkModePrimary
              : unreadMessagesPresent
                ? "#3EB1A6"
                : "#ef5866",
            color: dark ? theme.darkModeText : "white",
            textAlign: "center",
            overflowWrap: "break-word",
            cursor: "pointer",
          }}
          onClick={() =>
            history.push("/messages", {
              curPatient: patient,
            })
          }
        >
          {unreadMessagesPresent ? (
            <div>
              {t("dashboard.mailbox.new")}
              <img
                style={{
                  maxHeight: "30px",
                  maxWidth: "30px",
                  display: "inline-block",
                  paddingLeft: 10,
                }}
                src={emailAlert}
                alt="email alert"
              />
            </div>
          ) : (
            t("dashboard.mailbox.send_or_read")
          )}
        </h6>
      </div>
    </Col>
  );
  const ConditionalTbiSurvey = () => {
    const handleOpenNewTab = () => {
      const url =
        "https://forms.office.com/pages/responsepage.aspx?id=gxBGbdm66ECZbn5psfLnRwMgEdq_7OJJhXTsyptmlEdUN0lSQ09KSlVKMFg1QTBLNUZGVjI1VUFDTS4u";
      window.open(url, "_blank");
    };
    return (
      <Col md={3}>
        <div className="d-flex justify-content-center tooltip-container">
          <h6
            className="pt-2 pb-2 pl-3 pr-3"
            style={{
              width: "75%",
              height: "100%",
              borderRadius: "20px",
              backgroundColor: dark ? theme.darkModePrimary : "#7eaa54",
              color: dark ? theme.darkModeText : "white",
              textAlign: "center",
              overflowWrap: "break-word",
              cursor: "pointer",
            }}
            onClick={handleOpenNewTab}
          >
            Patient Summit Registration
          </h6>
          <span className="tooltip-text">
            Help us calculate the cost of your injury
          </span>
        </div>
      </Col>
    );
  };

  const VerifyEmailMessage =
    user.isVerified === false && user.userType !== "provider" && showPopUp ? (
      <Alert
        variant="warning"
        onClose={() => {
          setShowPopUp(false);
        }}
        dismissible
      >
        <Alert.Heading>{t("dashboard.verify_email.header")}</Alert.Heading>
        <p>{t("dashboard.verify_email.body.0")}</p>
        <p>
          {`${t("dashboard.verify_email.body.1")} ${user.email}${t("dashboard.verify_email.body.2")}`}
        </p>
        {/* Please verify your email address to access all of dashboard's
            features. We have sent an email to {user.email}. */}
      </Alert>
    ) : null;

  const getPossibleMessages = () => ({
    sallieText: {
      Stroke: t("dashboard.sallie.patient"),
      tbiPatient: t("dashboard.sallie.patient"),
      caregiver: t("dashboard.sallie.non_patient"),
      // For this to work properly you need to track " +
      // `${patient && patient.firstName}'s` +
      // " symptoms. Begin tracking below
      provider: t("dashboard.sallie.non_patient"),
    },
  });

  const getMessage = customizeMessages({ user, getPossibleMessages });

  if (width >= 768) {
    return (
      <>
        {VerifyEmailMessage}
        <Row>
          <Col md={9}>
            {user.userType === "provider" && (
              <Sallie
                user={user}
                text={getMessage("sallieText")}
                isProvider={true}
                variant="face"
              />
            )}
            {(user.userType === "caregiver" ||
              user.userType === "Therapist") && (
              <Sallie
                text={getMessage("sallieText")}
                isTPCG={true}
                variant="face"
              />
            )}
            {user.userType !== "provider" &&
              user.userType !== "caregiver" &&
              user.userType !== "Therapist" && (
                <Sallie text={getMessage("sallieText")} variant="face" />
              )}
          </Col>
          <Col>
            <ThemeChanger />
          </Col>
        </Row>
        {/* End top row */}
        <Row className="d-flex align-items-start justify-content-between mt-2">
          {ConditionalCurrentPatient()}
          {ConditionalReferPatientButton()}
          {ConditionalInviteTherapist()}
          {ConditionalInvitePatient()}
          {ConditionalInviteProvider()}
          {user.userType == "patient" && ConditionalSendMessagePatient()}
          {/* {(user.userType == "patient" || user.userType == "caregiver") && ConditionalTbiSurvey()} */}
        </Row>

        <Row>
          {/* One shorter left col and one larger right col */}
          <Col md={4}>
            {user.noPatientProvider !== "noPatient" && (
              <GoalList userType={user.userType} />
            )}
            {user.userType != "patient" && ConditionalSendMessage()}
            {user.userType != "provider" && <EditNotification />}
            <ConditionalCalendar />
            <div className="mt-3">
              {user.noPatientProvider !== "noPatient" &&
                patient &&
                user.userType !== "patient" &&
                user.userType !== "caregiver" && (
                  <DaysFromInjury patient={patient} />
                )}
              {ConditionalOnOffButtonMessages()}
              {ConditionalOnOffButtonAlert()}
              {ConditionalOnOffButtonReferralRecord()}
              {ConditionalPatientStatus()}
            </div>
          </Col>
          <Col md={8}>
            {ConditionalFeelingToday}
            {ConditionalSymptomTracker}
            {ConditionalAddSymptoms}
            {ConditionalCombinedChart}
            <SuggestionBox />
          </Col>
        </Row>
      </>
    );
  } else {
    return (
      <>
        {VerifyEmailMessage}
        <Row>
          <Col md={8}>
            {userType === "provider" && (
              <Sallie
                text={getMessage("sallieText")}
                isProvider={true}
                variant="face"
              />
            )}
            {(userType === "caregiver" || userType === "Therapist") && (
              <Sallie
                text={getMessage("sallieText")}
                isTPCG={true}
                variant="face"
              />
            )}
            {!(
              userType === "provider" ||
              userType === "caregiver" ||
              userType === "Therapist"
            ) && <Sallie text={getMessage("sallieText")} variant="face" />}
          </Col>
          <Col>
            <ThemeChanger />
          </Col>
        </Row>
        {/* End top row */}
        <Row className="d-flex align-items-center justify-content-center">
          {ConditionalCurrentPatient()}
          {ConditionalInviteProvider()}
          {ConditionalInvitePatient()}
          {ConditionalInviteTherapist()}
          {/* {(user.userType == "patient" || user.userType == "caregiver") && ConditionalTbiSurvey()} */}
          {ConditionalReferPatientButton()}
        </Row>
        <Row>
          {/* One shorter left col and one larger right col */}
          <Col md={4}>
            {user.noPatientProvider !== "noPatient" && (
              <GoalList userType={user.userType} />
            )}
            {user.userType != "provider" && <EditNotification />}
            <ConditionalCalendar />
            {user.noPatientProvider !== "noPatient" &&
              patient &&
              user.userType !== "patient" &&
              user.userType !== "caregiver" && (
                <DaysFromInjury patient={patient} />
              )}
            {ConditionalSendMessage()}
            {ConditionalOnOffButtonMessages()}
            {ConditionalOnOffButtonAlert()}
            {ConditionalOnOffButtonReferralRecord()}
            {ConditionalPatientStatus()}
          </Col>

          <Col md={8}>
            {ConditionalFeelingToday}
            {ConditionalSymptomTracker}
            {ConditionalAddSymptoms}
            {ConditionalCombinedChart}
            <SuggestionBox />
          </Col>
        </Row>
      </>
    );
  }
};

export default Dashboard;